'use client';

import { staticRoutes } from '@/constants/static-routes';
import { AnalyticsPageCategories } from '@/enums/analytics-type';
import { useProfile } from '@/hooks/profile/profile';
import { useAuth } from '@/hooks/use-auth';

export type FooterLinksStack = {
  headerText: string;
  links: FooterLink[];
};

export type FooterLink = {
  className?: string;
  dataLinkCategory: string;
  dataLinkName: string;
  externalLink: boolean;
  href: string;
  onClick?: () => void;
  text: string;
  useAnchor?: boolean;
};

type UseFooterLinksType = {
  footerLinks: FooterLinksStack[];
};

export const useFooterLinks = (): UseFooterLinksType => {
  const { data: profile } = useProfile();
  const { signIn, signOut, signUp } = useAuth();

  const accountAuthenticatedLinks: FooterLink[] = [
    {
      dataLinkCategory: AnalyticsPageCategories.FOOTER,
      dataLinkName: 'Tickets and Purchases',
      externalLink: false,
      href: '/account/purchases',
      text: 'Tickets and Purchases',
      useAnchor: true,
    },
    {
      dataLinkCategory: AnalyticsPageCategories.FOOTER,
      dataLinkName: 'Refunds',
      externalLink: false,
      href: '/refund-request',
      text: 'Refunds',
      useAnchor: true,
    },
    {
      dataLinkCategory: AnalyticsPageCategories.FOOTER,
      dataLinkName: 'Profile',
      externalLink: false,
      href: '/account/profile',
      text: 'Your Profile',
    },
    {
      dataLinkCategory: AnalyticsPageCategories.FOOTER,
      dataLinkName: 'Email Subscriptions',
      externalLink: false,
      href: '/account/subscriptions',
      text: 'Email Subscriptions',
    },
    {
      dataLinkCategory: AnalyticsPageCategories.FOOTER,
      dataLinkName: 'Your Gift Cards',
      externalLink: false,
      href: '/account/giftcards',
      text: 'Your Gift Cards',
      useAnchor: true,
    },
    {
      dataLinkCategory: AnalyticsPageCategories.FOOTER,
      dataLinkName: 'Watchlist',
      externalLink: false,
      href: '/account/watchlist',
      text: 'Your Watchlist',
    },
    {
      dataLinkCategory: AnalyticsPageCategories.FOOTER,
      dataLinkName: 'Sign Out',
      externalLink: false,
      href: '#',
      onClick: signOut,
      text: 'Sign Out',
      useAnchor: true,
    },
  ];

  const accountUnauthenticatedLinks: FooterLink[] = [
    {
      dataLinkCategory: AnalyticsPageCategories.FOOTER,
      dataLinkName: 'Sign In',
      externalLink: false,
      href: '#',
      onClick: signIn,
      text: 'Sign In',
      useAnchor: true,
    },
    {
      dataLinkCategory: AnalyticsPageCategories.FOOTER,
      dataLinkName: 'Register',
      externalLink: false,
      href: '#',
      onClick: signUp,
      text: 'Register',
      useAnchor: true,
    },
    {
      dataLinkCategory: AnalyticsPageCategories.FOOTER,
      dataLinkName: 'Refunds',
      externalLink: false,
      href: '/account/refunds',
      text: 'Refunds',
      useAnchor: true,
    },
  ];

  const footerLinks = [
    {
      headerText: 'Movies',
      links: [
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'Featured',
          externalLink: false,
          href: '/movies/featured',
          text: 'Featured',
          useAnchor: true,
        },
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'Now Playing',
          externalLink: false,
          href: '/movies/now-playing',
          text: 'Now Playing',
          useAnchor: true,
        },
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'Coming Soon',
          externalLink: false,
          href: '/movies/coming-soon',
          text: 'Coming Soon',
          useAnchor: true,
        },
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'CineArts',
          externalLink: false,
          href: '/movies/cinearts',
          text: 'CineArts',
          useAnchor: true,
        },
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'Cinemark XD',
          externalLink: false,
          href: '/movies/cinemark-xd',
          text: 'Cinemark XD',
          useAnchor: true,
        },
      ],
    },
    {
      headerText: 'Programs',
      links: [
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'Movie Rewards',
          externalLink: false,
          href: '/movie-rewards',
          text: 'Movie Rewards',
          useAnchor: true,
        },
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'Discounts',
          externalLink: false,
          href: '/discounts',
          text: 'Discounts',
          useAnchor: true,
        },
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'Prepaid Tickets',
          externalLink: false,
          href: '/prepaidtickets',
          text: 'Prepaid Tickets',
          useAnchor: true,
        },
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'Gift Cards',
          externalLink: false,
          href: '/gift-cards',
          text: 'Gift Cards',
          useAnchor: true,
        },
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'Food and Drinks',
          externalLink: false,
          href: '/food-and-drink',
          text: 'Food & Drinks',
          useAnchor: true,
        },
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'Private Events',
          externalLink: false,
          href: '/private-events',
          text: 'Private Events',
          useAnchor: true,
        },
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'Merchandise',
          externalLink: false,
          href: '/merchandise',
          text: 'Merchandise',
          useAnchor: true,
        },
      ],
    },
    {
      headerText: 'Technology',
      links: [
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'Theatre Technology',
          externalLink: false,
          href: '/technology',
          text: 'Theatre Technology',
          useAnchor: true,
        },
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'Cinemark XD',
          externalLink: false,
          href: '/technology/cinemark-xd',
          text: 'Cinemark XD',
          useAnchor: true,
        },
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'Luxury Loungers',
          externalLink: false,
          href: '/technology/luxury-loungers',
          text: 'Luxury Loungers',
          useAnchor: true,
        },
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'RealD 3D',
          externalLink: false,
          href: '/technology/reald-3d',
          text: 'RealD 3D',
          useAnchor: true,
        },
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'D-BOX',
          externalLink: false,
          href: '/technology/d-box',
          text: 'D-BOX',
          useAnchor: true,
        },
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'IMAX',
          externalLink: false,
          href: '/technology/imax',
          text: 'IMAX',
          useAnchor: true,
        },
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'SCREENX',
          externalLink: false,
          href: '/technology/screenx',
          text: 'SCREENX',
          useAnchor: true,
        },
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'Accessibility',
          externalLink: false,
          href: '/accessibility-at-cinemark',
          text: 'Accessibility',
          useAnchor: true,
        },
      ],
    },
    {
      headerText: 'Cinemark',
      links: [
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'About Cinemark',
          externalLink: false,
          href: '/about',
          text: 'About Cinemark',
          useAnchor: true,
        },
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'Gamescape',
          externalLink: false,
          href: '/gamescape',
          text: 'Gamescape',
          useAnchor: true,
        },
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'Movie News',
          externalLink: false,
          href: '/movie-news',
          text: 'Movie News',
          useAnchor: true,
        },
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'Guest Services',
          externalLink: false,
          href: '/guest-services',
          text: 'Guest Services',
          useAnchor: true,
        },
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'FAQ',
          externalLink: false,
          href: '/faq',
          text: 'FAQ',
          useAnchor: true,
        },
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'MPA Ratings',
          externalLink: true,
          href: 'http://www.filmratings.com',
          text: 'MPA Ratings',
          useAnchor: true,
        },
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'International',
          externalLink: false,
          href: '/about/international',
          text: 'International',
          useAnchor: true,
        },
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'Advertise with Us',
          externalLink: false,
          href: '/about/advertise-with-us',
          text: 'Advertise with Us',
          useAnchor: true,
        },
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'Investor Relations',
          externalLink: true,
          href: staticRoutes.investorRelations,
          text: 'Investor Relations',
          useAnchor: true,
        },
      ],
    },
    {
      headerText: 'Careers',
      links: [
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'Career Opportunities',
          externalLink: true,
          href: staticRoutes.careers,
          text: 'Career Opportunities',
          useAnchor: true,
        },
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'Employee Referrals',
          externalLink: true,
          href: staticRoutes.careers,
          text: 'Employee Referrals',
          useAnchor: true,
        },
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'LinkedIn',
          externalLink: true,
          href: staticRoutes.cinemarkLinkedIn,
          text: 'LinkedIn',
          useAnchor: true,
        },
      ],
    },
    {
      headerText: 'Account',
      links: [
        ...profile ? accountAuthenticatedLinks : accountUnauthenticatedLinks,
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'Movie Rewards',
          externalLink: false,
          href: '/movie-rewards',
          text: 'Cinemark Movie Rewards',
          useAnchor: true,
        },
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'Cinemark Privacy Notice',
          externalLink: false,
          href: '/privacy-policy',
          text: 'Cinemark Privacy Notice',
          useAnchor: true,
        },
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'Terms and Conditions',
          externalLink: false,
          href: '/terms-conditions',
          text: 'Cinemark Terms of Service',
          useAnchor: true,
        },
        {
          dataLinkCategory: AnalyticsPageCategories.FOOTER,
          dataLinkName: 'Manage My Personal Information',
          externalLink: false,
          href: '/privacy/do-not-sell',
          text: 'Manage My Personal Information',
          useAnchor: true,
        },
      ],
    },
  ];

  return { footerLinks };
};
